// frontend/src/modules/payroll/components/AddManualComponentDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { payrollApi } from '../api';
import type { ComponentType } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employees: EmployeeWithUser[];
  runId: string;
  onAdded: () => void;
}

const TYPES: ComponentType[] = ['allowance', 'bonus', 'deduction', 'overtime', 'base', 'penalty'];

export function AddManualComponentDialog({ open, onOpenChange, employees, runId, onAdded }: Props) {
  const [employeeId, setEmployeeId] = React.useState('');
  const [type, setType] = React.useState<ComponentType>('bonus');
  const [amount, setAmount] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setEmployeeId(employees[0]?.id ?? '');
      setType('bonus'); setAmount(''); setDescription('');
    }
  }, [open, employees]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employeeId || !amount || !description.trim()) {
      toast.error('Employee, amount, and description are required');
      return;
    }
    setBusy(true);
    try {
      await payrollApi.addManualComponent(runId, {
        employee_id: employeeId, component_type: type,
        amount: Number(amount), description: description.trim(),
      });
      toast.success('Line added');
      onAdded();
    } catch (err: any) {
      toast.error('Add failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add manual line</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ComponentType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Amount</Label>
              <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Eid bonus" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add line'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
